import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, CheckCircle2, RefreshCw, DollarSign } from 'lucide-react';

/**
 * /alternatives/funds/:fundId/wind-down
 *
 * GP-side wind-down workflow for European-waterfall funds. Operator clicks
 * "Compute Clawback" → server walks all commitments under the fund, calculates
 * GP-owed clawback per LP under European rules, and writes the result. The
 * operator then settles each per-commitment row by clicking Pay.
 */

interface ClawbackStatusRow {
  commitmentId: number;
  portfolioId: number;
  waterfallType: string;
  clawbackAmount: number | null;
  clawbackPaid: number | null;
  clawbackOutstanding: number | null;
  computedAt: string | null;
  paidAt: string | null;
}

const fmtMoney = (v: number | null | undefined) => {
  if (v == null || isNaN(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1e6) return `${v < 0 ? '-' : ''}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${v < 0 ? '-' : ''}$${(a / 1e3).toFixed(0)}K`;
  return `${v < 0 ? '-' : ''}$${a.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const FundWindDownScreen: React.FC = () => {
  const { fundId } = useParams<{ fundId: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ClawbackStatusRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyCommitmentId, setBusyCommitmentId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null);
  const [payModal, setPayModal] = useState<ClawbackStatusRow | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payError, setPayError] = useState<string | null>(null);

  const flash = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg });
    window.setTimeout(() => setToast(null), 4500);
  };

  const loadStatus = () => {
    if (!fundId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`/api/alternatives/phase3/funds/${fundId}/clawback-status`)
      .then((r) => setStatus(Array.isArray(r.data) ? r.data : []))
      .catch((err) =>
        setError(err?.response?.data?.message || err?.message || 'Failed to load status')
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundId]);

  const handleCompute = async () => {
    if (!fundId) return;
    if (!window.confirm(
      'Compute fund-end clawback for every European commitment in this fund?\n\n' +
      'This recalculates clawback_amount per commitment based on current ledger state. ' +
      'Already-settled clawback (clawback_paid) is preserved.'
    )) return;
    setComputing(true);
    try {
      await axios.post(`/api/alternatives/phase3/funds/${fundId}/wind-down/compute-clawback`);
      flash('success', 'Clawback computed across all European commitments.');
      loadStatus();
    } catch (err: any) {
      flash('error', err?.response?.data?.message || err?.message || 'Compute failed');
    } finally {
      setComputing(false);
    }
  };

  const handlePay = (row: ClawbackStatusRow) => {
    const owed = row.clawbackOutstanding || 0;
    if (owed <= 0) {
      flash('error', 'Nothing outstanding on this commitment.');
      return;
    }
    setPayError(null);
    setPayAmount('');
    setPayModal(row);
  };

  const submitPay = async () => {
    if (!payModal) return;
    const owed = payModal.clawbackOutstanding || 0;
    let amount: number | null = null;
    if (payAmount.trim() !== '') {
      const n = Number(payAmount.replace(/[$,]/g, ''));
      if (!isFinite(n) || n <= 0) {
        setPayError('Enter a positive amount, or leave blank to settle in full.');
        return;
      }
      if (n > owed) {
        setPayError(`Amount exceeds outstanding ${fmtMoney(owed)}.`);
        return;
      }
      amount = n;
    }
    setBusyCommitmentId(payModal.commitmentId);
    try {
      await axios.post(
        `/api/alternatives/phase3/commitments/${payModal.commitmentId}/clawback/pay`,
        { amount }
      );
      flash(
        'success',
        `Settled ${fmtMoney(amount ?? owed)} on commitment #${payModal.commitmentId}.`
      );
      setPayModal(null);
      loadStatus();
    } catch (err: any) {
      setPayError(err?.response?.data?.message || err?.message || 'Payment failed');
    } finally {
      setBusyCommitmentId(null);
    }
  };

  // Aggregate KPIs
  const europeanRows = status.filter((r) => r.waterfallType === 'EUROPEAN');
  const totalClawback = europeanRows.reduce((s, r) => s + (r.clawbackAmount || 0), 0);
  const totalOutstanding = europeanRows.reduce((s, r) => s + (r.clawbackOutstanding || 0), 0);
  const computedCount = europeanRows.filter((r) => r.computedAt).length;
  const settledCount = europeanRows.filter(
    (r) => (r.clawbackAmount || 0) > 0 && (r.clawbackOutstanding || 0) <= 0
  ).length;
  const outstandingCount = europeanRows.filter((r) => (r.clawbackOutstanding || 0) > 0).length;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-6 py-6">
      <div className="max-w-[1400px] mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-violet-400 font-bold mb-1">
              Fund Wind-down
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              Clawback computation • Fund #{fundId}
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-3xl">
              European-waterfall commitments only. Per LP, the GP must return any carried interest
              paid in excess of what whole-fund LP-first economics would have authorized at
              fund close. Compute updates each commitment&apos;s clawback amount; payment posts a
              GP→LP cash impact through the unified posting engine.
            </p>
          </div>
          <button
            onClick={handleCompute}
            disabled={computing}
            className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-lg shadow-violet-600/20"
          >
            <RefreshCw className={`w-4 h-4 ${computing ? 'animate-spin' : ''}`} />
            {computing ? 'Computing…' : 'Compute Clawback'}
          </button>
        </div>

        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-lg px-4 py-3 mb-4 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        {toast && (
          <div
            className={`fixed top-20 right-6 z-50 rounded-lg px-4 py-3 text-sm shadow-2xl border ${
              toast.tone === 'success'
                ? 'bg-emerald-900/90 border-emerald-500/40 text-emerald-100'
                : 'bg-rose-900/90 border-rose-500/40 text-rose-100'
            }`}
          >
            {toast.msg}
          </div>
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <Kpi label="European LPs" value={europeanRows.length.toString()} />
          <Kpi label="Computed" value={computedCount.toString()} />
          <Kpi
            label="Outstanding"
            value={outstandingCount.toString()}
            tone={outstandingCount > 0 ? 'warning' : 'neutral'}
          />
          <Kpi label="Settled" value={settledCount.toString()} tone="positive" />
          <Kpi label="Total clawback" value={fmtMoney(totalClawback)} />
          <Kpi
            label="Outstanding $"
            value={fmtMoney(totalOutstanding)}
            tone={totalOutstanding > 0 ? 'warning' : 'positive'}
          />
        </div>

        {/* Status table */}
        <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Per-commitment status</h2>
            <div className="text-xs text-slate-500">
              Showing {status.length} commitment{status.length === 1 ? '' : 's'}{' '}
              ({europeanRows.length} European)
            </div>
          </div>
          {loading ? (
            <div className="px-5 py-12 text-center text-slate-500 text-sm">Loading…</div>
          ) : status.length === 0 ? (
            <div className="px-5 py-12 text-center text-slate-500 text-sm">
              No commitments under this fund.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-950/40">
                  <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                    <th className="text-left px-5 py-2 font-semibold">Commitment</th>
                    <th className="text-left px-3 py-2 font-semibold">Portfolio</th>
                    <th className="text-left px-3 py-2 font-semibold">Waterfall</th>
                    <th className="text-right px-3 py-2 font-semibold">Clawback</th>
                    <th className="text-right px-3 py-2 font-semibold">Paid</th>
                    <th className="text-right px-3 py-2 font-semibold">Outstanding</th>
                    <th className="text-left px-3 py-2 font-semibold">Computed</th>
                    <th className="text-left px-3 py-2 font-semibold">Paid at</th>
                    <th className="text-right px-5 py-2 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {status.map((r) => {
                    const isEuropean = r.waterfallType === 'EUROPEAN';
                    const owed = r.clawbackOutstanding || 0;
                    const settled =
                      isEuropean && (r.clawbackAmount || 0) > 0 && owed <= 0;
                    return (
                      <tr
                        key={r.commitmentId}
                        className="border-t border-white/5 hover:bg-white/5"
                      >
                        <td className="px-5 py-2.5 font-medium text-slate-200">
                          #{r.commitmentId}
                        </td>
                        <td className="px-3 py-2.5 text-slate-300">P{r.portfolioId}</td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                              isEuropean
                                ? 'bg-violet-500/20 text-violet-300'
                                : 'bg-slate-700/40 text-slate-400'
                            }`}
                          >
                            {r.waterfallType}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right text-slate-200 font-mono">
                          {fmtMoney(r.clawbackAmount)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-slate-300 font-mono">
                          {fmtMoney(r.clawbackPaid)}
                        </td>
                        <td
                          className={`px-3 py-2.5 text-right font-mono font-semibold ${
                            owed > 0 ? 'text-amber-300' : 'text-slate-500'
                          }`}
                        >
                          {fmtMoney(r.clawbackOutstanding)}
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 text-[11px]">
                          {r.computedAt ? new Date(r.computedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 text-[11px]">
                          {r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          {!isEuropean ? (
                            <span className="text-[11px] text-slate-600">n/a</span>
                          ) : settled ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
                              <CheckCircle2 className="w-3 h-3" />
                              Settled
                            </span>
                          ) : owed > 0 ? (
                            <button
                              onClick={() => handlePay(r)}
                              disabled={busyCommitmentId === r.commitmentId}
                              className="inline-flex items-center gap-1 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 px-2.5 py-1 rounded text-[11px] font-semibold disabled:opacity-50"
                            >
                              <DollarSign className="w-3 h-3" />
                              {busyCommitmentId === r.commitmentId ? 'Paying…' : 'Pay'}
                            </button>
                          ) : (
                            <span className="text-[11px] text-slate-600">No clawback</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-slate-500 max-w-3xl">
          <strong className="text-slate-400">How it works.</strong> European waterfall enforces
          LP-first whole-fund economics: the LP must receive return of capital + accrued preferred
          return before the GP earns any carry. If interim distributions overpaid carry to the GP,
          the difference must be returned at fund close. American (deal-by-deal) commitments are
          excluded — carry is settled per deal as it&apos;s realized.
        </div>
      </div>

      {payModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setPayModal(null)}>
          <div
            className="bg-slate-900 border border-white/10 rounded-xl p-6 w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-widest text-violet-400 font-bold mb-1">
              Clawback Payment
            </div>
            <h2 className="text-lg font-bold text-white mb-1">
              Commitment #{payModal.commitmentId}
            </h2>
            <p className="text-sm text-slate-400 mb-4">
              Outstanding: <span className="text-white font-semibold">{fmtMoney(payModal.clawbackOutstanding || 0)}</span>
            </p>
            <label className="block text-xs text-slate-400 mb-1">
              Payment amount — leave blank to settle in full
            </label>
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              value={payAmount}
              onChange={(e) => { setPayAmount(e.target.value); setPayError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') submitPay(); }}
              placeholder={fmtMoney(payModal.clawbackOutstanding || 0)}
              className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-violet-500/60 mb-2"
            />
            {payError && <div className="text-xs text-rose-400 mb-2">{payError}</div>}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setPayModal(null)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-300 border border-white/10 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={submitPay}
                disabled={busyCommitmentId === payModal.commitmentId}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-50"
              >
                {busyCommitmentId === payModal.commitmentId
                  ? 'Paying…'
                  : payAmount.trim() === '' ? 'Settle in Full' : 'Pay'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Kpi: React.FC<{
  label: string;
  value: string;
  tone?: 'neutral' | 'positive' | 'warning';
}> = ({ label, value, tone = 'neutral' }) => {
  const color =
    tone === 'positive' ? 'text-emerald-300' : tone === 'warning' ? 'text-amber-300' : 'text-white';
  return (
    <div className="bg-slate-900/40 border border-white/5 rounded-lg px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
        {label}
      </div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
};

export default FundWindDownScreen;
